import React from "react";
import { Col, Divider, Modal, Row, Spin } from "antd";
import {
  GetSignInfoQueryParams,
  useGetSignInfoQuery,
} from "../../../../store/api/applicationBankApi/signing.api";
import { useGetApplicationDataToBankQuery } from "../../../../store/api/applicationBankApi/applicationBank.api";
import { useParams } from "react-router-dom";
import { APPLICATION_BANK_STATUS } from "../../../../utils/guards/ApplicationBankStatusGuard/types";

type SignInfoModalProps = {
  queryParams: GetSignInfoQueryParams;
  isOpened: boolean;
  onClose: () => void;
};

const orDash = (value?: string) => (value ? value : "—");

export const SignInfoModal = ({
  isOpened,
  onClose,
  queryParams,
}: SignInfoModalProps) => {
  const {
    data: signInfo,
    isFetching,
    isError,
  } = useGetSignInfoQuery(queryParams);

  const { applicationBankId } = useParams();
  const { data: applicationBank } = useGetApplicationDataToBankQuery(
    Number(applicationBankId)
  );

  const isNewApplication =
    applicationBank?.modified_bank_status.type ===
    APPLICATION_BANK_STATUS.NEW_APPLICATION;

  return (
    <Modal
      open={isOpened}
      title={<h2>Проверка ЭЦП</h2>}
      width={600}
      footer={false}
      onCancel={onClose}
      styles={{
        body: {
          height: 500,
          maxHeight: "calc(100vh - 300px)",
          overflowY: "auto",
          overflowX: "hidden",
          padding: 10,
        },
      }}
    >
      {isFetching && (
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {isNewApplication && !isFetching && (
        <div>Отказано в доступе - заявка не принята в работу</div>
      )}
      {isError && !isNewApplication && <div>Не удалось получить данные</div>}

      {!isFetching && !!signInfo && (
        <>
          <h3>Субъект</h3>

          <Row>
            <Col span={7}>Владелец подписи</Col>
            <Col span={17}>
              {orDash(
                [signInfo.user_name, signInfo.G].filter(Boolean).join(" ")
              )}
            </Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Должность</Col>
            <Col span={17}>{orDash(signInfo.job_title)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Организация</Col>
            <Col span={17}>{orDash(signInfo.organization_name)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Страна</Col>
            <Col span={17}>{orDash(signInfo.country)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Город</Col>
            <Col span={17}>{orDash(signInfo.city)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Адрес</Col>
            <Col span={17}>{orDash(signInfo.address)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Электронная почта</Col>
            <Col span={17}>{orDash(signInfo.email)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>ИНН</Col>
            <Col span={17}>{orDash(signInfo.inn)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>ОГРН</Col>
            <Col span={17}>{orDash(signInfo.ogrn)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>СНИЛС</Col>
            <Col span={17}>{orDash(signInfo.snils)}</Col>
          </Row>

          <h3>Издатель</h3>

          <Row>
            <Col span={7}>Организация</Col>
            <Col span={17}>{orDash(signInfo.issuerName?.cn)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Адрес</Col>
            <Col span={17}>{orDash(signInfo.issuerName?.street)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Город</Col>
            <Col span={17}>{orDash(signInfo.issuerName?.l)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Страна</Col>
            <Col span={17}>{orDash(signInfo.issuerName?.c)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>ИНН ЮЛ</Col>
            <Col span={17}>{orDash(signInfo.issuerName?.ul)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Электронная почта</Col>
            <Col span={17}>{orDash(signInfo.issuerName?.e)}</Col>
          </Row>

          <h3>Сертификат</h3>

          <Row>
            <Col span={7}>Серийный номер</Col>
            <Col span={17}>{orDash(signInfo.serialNumber)}</Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Срок действия</Col>
            <Col span={17}>
              {signInfo.fromDate?.data && signInfo.toDate?.data
                ? `${signInfo.fromDate.data} – ${signInfo.toDate.data}`
                : "—"}
            </Col>
          </Row>

          <Divider style={{ margin: 5 }}></Divider>

          <Row>
            <Col span={7}>Отпечаток сертификата</Col>
            <Col span={17}>{orDash(signInfo.thumbprint)}</Col>
          </Row>
        </>
      )}
    </Modal>
  );
};
